import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Paper, IconButton, Skeleton } from "@mui/material";
import { ChevronLeftOutlined, ChevronRightOutlined } from "@mui/icons-material";
import apiService from "../../../service/Api";

interface CalendarStream {
  stream_id: number;
  booking_id: number;
  title: string;
  date: string;
  start_time: string;
  entry_type: string;
}

interface WeekCalendarProps {
  onStreamClick?: (streamId: number) => void;
}

const getWeekNumber = (date: Date): number => {
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear =
    (date.getTime() - firstDayOfYear.getTime()) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
};

const getWeekDates = (weekNumber: number, year: number): Date[] => {
  const firstDayOfYear = new Date(year, 0, 1);
  const daysOffset = (weekNumber - 1) * 7 - firstDayOfYear.getDay() + 1;
  const weekStart = new Date(year, 0, 1 + daysOffset);

  const dates: Date[] = [];
  for (let i = 0; i < 5; i++) {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    dates.push(date);
  }
  return dates;
};

const dayNames = ["Mon", "Tue", "Wed", "Thur", "Fri"];

const WeekCalendar: React.FC<WeekCalendarProps> = ({ onStreamClick }) => {
  const today = new Date();
  const [currentWeek, setCurrentWeek] = useState<number>(
    getWeekNumber(today),
  );
  const [currentYear, setCurrentYear] = useState<number>(today.getFullYear());
  const [calendarStreams, setCalendarStreams] = useState<CalendarStream[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const weekDates = getWeekDates(currentWeek, currentYear);

  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.get<CalendarStream[]>(
        `/api/streams/stream/stream-calendar/?week=${currentWeek}&year=${currentYear}`,
      );
      setCalendarStreams(response.data);
    } catch (error) {
      console.error("Failed to fetch stream calendar data:", error);
      setCalendarStreams([]);
    } finally {
      setLoading(false);
    }
  }, [currentWeek, currentYear]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const handlePreviousWeek = () => {
    if (currentWeek === 1) {
      setCurrentWeek(52);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentWeek(currentWeek - 1);
    }
  };

  const handleNextWeek = () => {
    if (currentWeek === 52) {
      setCurrentWeek(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentWeek(currentWeek + 1);
    }
  };

  const getMonthYearLabel = (): string => {
    if (weekDates.length > 0) {
      return weekDates[0].toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    }
    return "";
  };

  const isToday = (date: Date): boolean =>
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: "#F8F9FA",
        borderRadius: "12px",
        padding: "20px",
        minWidth: "280px",
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
          {getMonthYearLabel()}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton size="small" onClick={handlePreviousWeek}>
            <ChevronLeftOutlined />
          </IconButton>
          <IconButton size="small" onClick={handleNextWeek}>
            <ChevronRightOutlined />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          borderBottom: "1px solid #E0E0E0",
          pb: 1,
        }}
      >
        {weekDates.map((date, index) => {
          const isTodayDate = isToday(date);
          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "8px 12px",
                borderRadius: "8px",
                backgroundColor: isTodayDate ? "#8B6914" : "transparent",
                color: isTodayDate ? "#ffffff" : "#333",
                minWidth: "45px",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {date.getDate()}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {dayNames[index]}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, color: "text.primary", mb: 2 }}
      >
        In the next 10 days
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {loading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Box key={index} sx={{ display: "flex", gap: 2 }}>
              <Skeleton variant="rounded" width={50} height={60} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="60%" />
              </Box>
            </Box>
          ))
        ) : calendarStreams.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", textAlign: "center", py: 2 }}
          >
            No streams in this week
          </Typography>
        ) : (
          calendarStreams.slice(0, 5).map((stream) => (
            <Box
              key={stream.stream_id}
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                padding: "8px 0",
                cursor: onStreamClick ? "pointer" : "default",
                "&:hover": {
                  backgroundColor: onStreamClick
                    ? "rgba(0,0,0,0.04)"
                    : "transparent",
                },
                borderRadius: "8px",
              }}
              onClick={() => onStreamClick && onStreamClick(stream.stream_id)}
            >
              <Box
                sx={{
                  backgroundColor: "#E8F0FE",
                  borderRadius: "8px",
                  padding: "6px 12px",
                  minWidth: "60px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontSize: "10px",
                    lineHeight: 1,
                  }}
                >
                  {new Date(stream.date).toLocaleDateString("en-US", {
                    weekday: "short",
                  })}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  {new Date(stream.date).getDate()}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {(stream.title || "").replace(/^Stream for\s*/i, "")}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Stream
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Paper>
  );
};

export default WeekCalendar;
